import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { PUBLIC_API_URL, PublicLegalContent } from "./publicApi";
import "./Public.scss";

type PublicLegalPageProps = {
  apiPath: string;
  fallbackTitleEn: string;
  fallbackTitleAr: string;
};

const PublicLegalPage: React.FC<PublicLegalPageProps> = ({
  apiPath,
  fallbackTitleEn,
  fallbackTitleAr,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );
  const isArabic = searchParams.get("lang") === "ar";
  const [content, setContent] = useState<PublicLegalContent | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(false);

    fetch(`${PUBLIC_API_URL}${apiPath}?lang=${isArabic ? "ar" : "en"}`, {
      signal: controller.signal,
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error(`Request failed with ${res.status}`);
        }
        return res.json();
      })
      .then((json) => {
        setContent((json?.data as PublicLegalContent) ?? null);
        setLoading(false);
      })
      .catch((err) => {
        if (err?.name !== "AbortError") {
          setError(true);
          setLoading(false);
        }
      });

    return () => controller.abort();
  }, [apiPath, isArabic]);

  const toggleLanguage = () => {
    const next = new URLSearchParams(location.search);
    if (isArabic) {
      next.delete("lang");
    } else {
      next.set("lang", "ar");
    }
    const query = next.toString();
    navigate(`${location.pathname}${query ? `?${query}` : ""}`, { replace: true });
  };

  const title =
    content?.title || (isArabic ? fallbackTitleAr : fallbackTitleEn);
  const html = content?.html || "";

  return (
    <main className="public-container property-page" dir={isArabic ? "rtl" : "ltr"}>
      <header className="public-header">
        <a className="public-brand" href="/">
          <img
            className="brand-logo brand-logo--header"
            src="/static/images/brand-logo.png"
            alt="4Homes"
          />
        </a>
        <div className="header-actions">
          <button type="button" className="language-switch" onClick={toggleLanguage}>
            {isArabic ? "English" : "العربية"}
          </button>
        </div>
      </header>

      <div className="legal-shell">
        <article className="legal-card">
          {loading ? (
            <div className="loader">{isArabic ? "جاري التحميل..." : "Loading..."}</div>
          ) : error ? (
            <div className="legal-empty">
              {isArabic ? "تعذر تحميل المحتوى." : "Unable to load content."}
            </div>
          ) : html ? (
            <>
              <h1 className="legal-title">{title}</h1>
              <div
                className="legal-content"
                dangerouslySetInnerHTML={{ __html: html }}
              />
            </>
          ) : (
            <div className="legal-empty">
              {isArabic ? "لا توجد بيانات." : "No content available."}
            </div>
          )}
        </article>
      </div>
    </main>
  );
};

export default PublicLegalPage;
